var _a;
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { EMobileCandidateLists, isAcclaimedContest, translate } from "@sequentech/ui-core";
import { Icon } from "@sequentech/ui-essentials";
import { useTranslation } from "react-i18next";
import { sortCandidatesInBallotOrder, sortContestsInBallotOrder } from "../../services/ContestOrder";
import { getSlateName } from "../../services/Slates";
import { ESlateSelectionStatus, getSlateMembers, getSlateSelectionSummary, } from "../../services/SlateSelection";
import { selectBallotSelectionByElectionId } from "../../store/ballotSelections/ballotSelectionsSlice";
import { useAppSelector } from "../../store/hooks";
import { SlateApplyAction } from "./SlateApplyAction";
import { SlateCandidateList } from "./SlateCandidateList";
import { ESlateCardState, SlateChooser } from "./SlateChooser";
import { SlateMember } from "./SlateMember";
import { SlateSelectionStatus } from "./SlateSelectionStatus";
var AppliedNotice = styled("div")(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            "&:not(:empty)": {
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                margin: "0 0 20px",
                padding: "13px 15px",
                border: "1px solid #bee7d4",
                borderRadius: "7px",
                background: "#ecfdf5",
                color: "#065f46",
                fontSize: "14px",
                lineHeight: 1.45,
                overflowWrap: "anywhere",
            },
            "& .slate-applied-notice-icon": {
                marginTop: "3px",
            }
        },
        _b[theme.breakpoints.down("sm")] = {
            "&:not(:empty)": {
                fontSize: "13px",
                padding: "11px 12px",
                gap: "8px",
                marginBottom: "16px",
            },
        },
        _b);
});
var AllSelections = styled("div")(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            "display": "grid",
            "gridTemplateColumns": "repeat(var(--office-columns), minmax(0, 1fr))",
            "gap": "15px",
            "marginTop": "24px",
            "padding": "19px",
            "borderRadius": "8px",
            "background": theme.palette.lightBackground,
            "overflowWrap": "anywhere",
            "& .slate-all-selections-office": {
                display: "block",
                marginBottom: "7px",
                fontSize: "12px",
                color: theme.palette.customGrey.dark,
            },
            "& .slate-all-selections-name": {
                display: "block",
                fontSize: "13px",
                lineHeight: 1.55,
            }
        },
        _b[theme.breakpoints.down("sm")] = {
            "gridTemplateColumns": "1fr 1fr",
            "padding": "14px",
            "marginTop": "19px",
            "& .slate-all-selections-office": { fontSize: "11px" },
            "& .slate-all-selections-name": { fontSize: "12px" },
        },
        _b);
});
var MAX_OFFICE_COLUMNS = 5;
var CARD_STATES = (_a = {},
    _a[ESlateSelectionStatus.NONE] = ESlateCardState.NONE,
    _a[ESlateSelectionStatus.PARTLY] = ESlateCardState.SOME,
    _a[ESlateSelectionStatus.MIXED] = ESlateCardState.SOME,
    _a[ESlateSelectionStatus.ALL] = ESlateCardState.EXACT,
    _a);
/**
 * The slates of the ballot with what the voter currently holds of each one:
 * the selected members are marked and each slate says how much of it is
 * selected. Everything is read from the candidate selections of the ballot.
 */
export var SlateSelectionChooser = function (_a) {
    var _b;
    var ballotStyle = _a.ballotStyle, slates = _a.slates, defaultLanguage = _a.defaultLanguage, onEditSelections = _a.onEditSelections;
    var _c = useTranslation(), t = _c.t, i18n = _c.i18n;
    var selection = useAppSelector(selectBallotSelectionByElectionId(ballotStyle.election_id));
    var _d = useState(), appliedSlate = _d[0], setAppliedSlate = _d[1];
    var summaries = useMemo(function () {
        return new Map(slates.slates.map(function (slate) { return [
            slate.id,
            getSlateSelectionSummary(getSlateMembers(slate), selection),
        ]; }));
    }, [slates, selection]);
    var contests = useMemo(function () {
        return sortContestsInBallotOrder(ballotStyle.ballot_eml.contests).filter(function (contest) { return !isAcclaimedContest(contest); });
    }, [ballotStyle]);
    var selectedNames = function (contestId) {
        var contest = contests.find(function (entry) { return entry.id === contestId; });
        var entry = selection === null || selection === void 0 ? void 0 : selection.find(function (item) { return item.contest_id === contestId; });
        if (!contest) {
            return [];
        }
        return sortCandidatesInBallotOrder(contest, contest.candidates)
            .filter(function (candidate) {
            return entry === null || entry === void 0 ? void 0 : entry.choices.some(function (choice) { return choice.id === candidate.id && choice.selected > -1; });
        })
            .map(function (candidate) { var _a; return (_a = translate(candidate, "name", i18n.language)) !== null && _a !== void 0 ? _a : candidate.id; });
    };
    var hasSelection = contests.some(function (contest) { return selectedNames(contest.id).length > 0; });
    var isAppliedSlateHeld = !!appliedSlate && ((_b = summaries.get(appliedSlate.id)) === null || _b === void 0 ? void 0 : _b.status) === ESlateSelectionStatus.ALL;
    var appliedNotice = (function () {
        if (!appliedSlate || !isAppliedSlateHeld) {
            return null;
        }
        var name = getSlateName(appliedSlate, i18n.language, defaultLanguage);
        var blankOffices = contests.filter(function (contest) { return !appliedSlate.contests.some(function (entry) { return entry.contest.id === contest.id; }); });
        return blankOffices.length
            ? t("slates.apply.appliedPartial", {
                slate: name,
                count: blankOffices.length,
                offices: blankOffices
                    .map(function (contest) { var _a; return (_a = translate(contest, "name", i18n.language)) !== null && _a !== void 0 ? _a : contest.id; })
                    .join(", "),
            })
            : t("slates.apply.applied", { slate: name });
    })();
    return (_jsxs(_Fragment, { children: [_jsx(AppliedNotice, { className: "slate-applied-notice", role: "status", children: appliedNotice ? (_jsxs(_Fragment, { children: [_jsx(Icon, { className: "slate-applied-notice-icon", icon: faCheck, "aria-hidden": "true" }), _jsx("span", { className: "slate-applied-notice-text", children: appliedNotice })] })) : null }), _jsx(SlateChooser, { slates: slates, defaultLanguage: defaultLanguage, getCardState: function (slate) { var _a, _b; return CARD_STATES[(_b = (_a = summaries.get(slate.id)) === null || _a === void 0 ? void 0 : _a.status) !== null && _b !== void 0 ? _b : ESlateSelectionStatus.NONE]; }, renderSummary: function (slate) {
                    var summary = summaries.get(slate.id);
                    return summary ? _jsx(SlateSelectionStatus, { summary: summary }) : null;
                }, renderMemberLists: function (slate, lists) { return (_jsx(SlateCandidateList, { electionId: ballotStyle.election_id, slateId: slate.id, defaultExpanded: slates.mobileCandidateLists === EMobileCandidateLists.EXPANDED, children: lists })); }, renderMember: function (slate, _slateContest, candidate) {
                    var _a, _b;
                    return (_jsx(SlateMember, { candidateId: candidate.id, selected: (_b = (_a = summaries.get(slate.id)) === null || _a === void 0 ? void 0 : _a.selectedMemberIds.includes(candidate.id)) !== null && _b !== void 0 ? _b : false, children: translate(candidate, "name", i18n.language) }));
                }, renderActions: function (slate) {
                    var _a, _b;
                    return (_jsx(SlateCardActions, { isFullySelected: ((_a = summaries.get(slate.id)) === null || _a === void 0 ? void 0 : _a.status) === ESlateSelectionStatus.ALL, onEditSelections: function () { return onEditSelections(slate); }, children: _jsx(SlateApplyAction, { ballotStyle: ballotStyle, slate: slate, onApplied: setAppliedSlate, isFullySelected: ((_b = summaries.get(slate.id)) === null || _b === void 0 ? void 0 : _b.status) === ESlateSelectionStatus.ALL, onEditSelections: onEditSelections }) }));
                } }), hasSelection ? (_jsx(AllSelections, { className: "slate-all-selections", role: "group", "aria-label": t("slates.allSelections.label"), style: {
                    "--office-columns": Math.min(MAX_OFFICE_COLUMNS, contests.length),
                }, children: contests.map(function (contest) {
                    var names = selectedNames(contest.id);
                    return (_jsxs("div", { className: "slate-all-selections-contest", "data-contest-id": contest.id, children: [_jsx("span", { className: "slate-all-selections-office", children: translate(contest, "name", i18n.language) }), (names.length ? names : [t("slates.allSelections.none")]).map(function (name, index) { return (_jsx("span", { className: "slate-all-selections-name", children: name }, index)); })] }, contest.id));
                }) })) : null] }));
};
/**
 * Selecting a slate that is already fully selected would change nothing, so
 * the card offers to edit the selections instead. The apply action stays
 * mounted with its button hidden.
 */
var SlateCardActions = function (_a) {
    var isFullySelected = _a.isFullySelected, onEditSelections = _a.onEditSelections, children = _a.children;
    var t = useTranslation().t;
    var containerRef = useRef(null);
    var editRef = useRef(null);
    useLayoutEffect(function () {
        var _a, _b;
        if (isFullySelected && ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement))) {
            (_b = editRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }, [isFullySelected]);
    return (_jsxs(Box, { className: "slate-card-actions", ref: containerRef, sx: isFullySelected ? { "& .slate-apply-button": { display: "none" } } : undefined, children: [isFullySelected ? (_jsx(Button, { className: "slate-edit-selections-button", variant: "secondary", ref: editRef, onClick: onEditSelections, startIcon: _jsx(Icon, { className: "slate-edit-selections-icon", icon: faCheck, "aria-hidden": "true" }), children: t("slates.selection.edit") })) : null, children] }));
};
